import { useSyncExternalStore } from 'react';
import { apiClient } from '../api/client';
import { getShared } from './sharedSettings';
import { settingKeysFor } from './templateKeys';

/**
 * Podpis prodejce („za autobazar“) ve smlouvách.
 *
 * Podpis žije na serveru (Nastavení → Podpis prodejce). Po přihlášení se načte ten, který
 * smí právě tento uživatel použít (podle režimu: jeden za bazar / každý uživatel svůj),
 * a drží se v paměti, aby ho generátory dokumentů mohly číst synchronně – stejně jako šablony.
 *
 * Do textu šablony se vkládá jen značka SELLER_SIG_TOKEN. Teprve vykreslení do HTML
 * (tisk, náhled) ji nahradí obrázkem, do prostého textu pro e-podpis se přepíše na větu
 * „podepsáno za prodávajícího“.
 */

export const SELLER_SIG_TOKEN = '[[SELLER_SIGNATURE]]';

export interface SellerSigInfo { dataUrl: string; signerName: string }

export interface SellerSigState {
  mode: 'COMPANY' | 'USER';
  canUse: boolean;
  canManageCompany: boolean;
  company: { has: boolean; signerName: string; updatedAt: string | null; dataUrl: string | null };
  mine: { has: boolean; signerName: string; updatedAt: string | null; dataUrl: string | null; defaultName: string };
  effective: { available: boolean; source: 'COMPANY' | 'USER'; signerName: string; dataUrl: string | null };
}

let state: SellerSigState | null = null;
const listeners = new Set<() => void>();

export function setSellerSigState(s: SellerSigState | null): void {
  state = s;
  listeners.forEach(fn => fn());
}
export function getSellerSigState(): SellerSigState | null { return state; }
export function subscribeSellerSig(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Načte stav ze serveru. Když modul E-podpis není zapnutý, server vrátí 404 a podpis prostě není. */
export async function loadSellerSignature(): Promise<void> {
  try {
    const r = await apiClient.get('/signatures/state');
    setSellerSigState(r.data.data as SellerSigState);
  } catch {
    setSellerSigState(null);
  }
}

const PNG_DATA_URL = /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/;

/** Podpis použitelný pro vložení do dokumentu, nebo null. */
export function getSellerSignature(): SellerSigInfo | null {
  const e = state?.effective;
  if (!e?.available || !e.dataUrl || !PNG_DATA_URL.test(e.dataUrl)) return null;
  return { dataUrl: e.dataUrl, signerName: e.signerName };
}

/** Reaktivní stav podpisu pro obrazovky (Nastavení, editor šablon, podpisové okno). */
export function useSellerSigState(): SellerSigState | null {
  return useSyncExternalStore(subscribeSellerSig, getSellerSigState, getSellerSigState);
}

export const sellerSigKeysFor = settingKeysFor;

/** Má šablona zapnuté vkládání podpisu prodejce? (volba v Nastavení → Šablony dokumentů) */
export function sellerSigEnabledFor(templateKey?: string | null): boolean {
  return !!templateKey && sellerSigKeysFor(templateKey).some(k => getShared(`sellersig_${k}`) === '1');
}

/** Řádky, na které se podpis prodejce vkládá automaticky (strana prodávajícího / komisionáře). */
const SELLER_LINE = /(Prodávající|Komisionář|Předal)(\s*:\s*)\.{5,}/g;

/**
 * Vyplněný text šablony: tam, kde je řádek „Prodávající: ……“, se tečky nahradí značkou podpisu.
 * Dělá se jen když má šablona volbu zapnutou, podpis existuje a šablona nemá vlastní {{seller_signature}}.
 */
export function applySellerSignature(filled: string, templateKey?: string | null, template?: string, enabled?: boolean): string {
  if (!(enabled ?? sellerSigEnabledFor(templateKey)) || !getSellerSignature()) return filled;
  if (template && template.includes('{{seller_signature}}')) return filled;
  return filled.replace(SELLER_LINE, (_m, who: string, sep: string) => `${who}${sep}${SELLER_SIG_TOKEN}`);
}

/** Hodnota proměnné {{seller_signature}} (vlastní umístění podpisu v šabloně). */
export function sellerSignatureVar(): string {
  return getSellerSignature() ? SELLER_SIG_TOKEN : '';
}

/** Má šablona zapnuté vkládání podpisu zákazníka na řádek podpisu po e-podpisu? (výchozí ano) */
export function signerSigEnabledFor(templateKey?: string | null): boolean {
  if (!templateKey) return true;
  return !settingKeysFor(templateKey).some(k => getShared(`signersig_${k}`) === '0');
}

export const SIGNER_SIG_TOKEN = '[[SIGNER_SIGNATURE]]';

/** Řádky podpisu zákazníka / majitele (komitenta) / přebírajícího. */
const SIGNER_LINE = /(Kupující|Komitent|Převzal|Zákazník)(\s*:\s*)\.{5,}/g;

/**
 * Pro PODEPSANOU verzi: tečky za „Kupující: ……“ se nahradí značkou podpisu zákazníka.
 * U komisní smlouvy podepisuje majitel vozu (Komitent), jinde Kupující / Převzal.
 */
export function applySignerSignature(filled: string, templateKey?: string | null, template?: string, enabled?: boolean): string {
  if (!(enabled ?? signerSigEnabledFor(templateKey))) return filled;
  if (template && template.includes('{{signer_signature}}')) return filled;
  const commission = templateKey === 'COMMISSION_CONTRACT';
  return filled.replace(SIGNER_LINE, (m, who: string, sep: string) => {
    if (commission ? who !== 'Komitent' : who === 'Komitent') return m;
    return `${who}${sep}${SIGNER_SIG_TOKEN}`;
  });
}

const escAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** Podpis do dokumentu: obrázek (PNG data URL ze serveru) a jméno. */
export interface SigItem { dataUrl: string; name: string }
/** Kontext vykreslení: `seller` undefined = použij aktuální podpis prodejce z paměti, null = žádný. */
export interface SigCtx { seller?: SigItem | null; signer?: SigItem | null }

const sigImg = (item: SigItem, cls: string, label: string): string =>
  PNG_DATA_URL.test(item.dataUrl)
    ? `<img class="${cls}" data-${cls}="${escAttr(item.name)}" alt="${label}" src="${item.dataUrl}" `
      + `style="height:46px;width:auto;vertical-align:middle;margin:0 6px;mix-blend-mode:multiply"/>`
    : '';

/** Do už escapovaného HTML dosadí obrázky podpisů místo značek. Bez podpisu značku jen odstraní. */
export function injectSigTokens(escapedHtml: string, ctx?: SigCtx): string {
  let out = escapedHtml;
  if (out.includes(SELLER_SIG_TOKEN)) {
    const cur = getSellerSignature();
    const item = ctx && ctx.seller !== undefined ? ctx.seller : (cur ? { dataUrl: cur.dataUrl, name: cur.signerName } : null);
    out = out.split(SELLER_SIG_TOKEN).join(item ? sigImg(item, 'seller-sig', 'podpis prodejce') : '');
  }
  if (out.includes(SIGNER_SIG_TOKEN)) {
    const item = ctx?.signer ?? null;
    out = out.split(SIGNER_SIG_TOKEN).join(item ? sigImg(item, 'signer-sig', 'podpis zákazníka') : '');
  }
  return out;
}

/** Zpětně kompatibilní název (jen podpis prodejce z paměti). */
export const injectSellerSig = (escapedHtml: string): string => injectSigTokens(escapedHtml);

/** Začátek věty, kterou se podpis prodejce zapisuje do prostého textu určeného k podpisu zákazníkem. */
export const SELLER_TEXT_MARK = '[podpis prodejce';

/** Do prostého textu (e-podpis) se místo obrázku zapíše věta, kterou přečte i zákazník. */
export function sellerSigTextOf(name: string): string {
  const n = name.replace(/[\[\]\r\n]+/g, ' ').trim();
  return n ? `${SELLER_TEXT_MARK}: ${n}]` : `${SELLER_TEXT_MARK}]`;
}

/** Značku podpisu v prostém textu nahradí větou „[podpis prodejce: Jméno]“ (nebo ji odstraní, když podpis není). */
export function sellerSigTokenToText(text: string): string {
  let out = text.split(SIGNER_SIG_TOKEN).join('');
  if (!out.includes(SELLER_SIG_TOKEN)) return out;
  const sig = getSellerSignature();
  return out.split(SELLER_SIG_TOKEN).join(sig ? sellerSigTextOf(sig.signerName) : '');
}

const DOTS = '.............................';

/**
 * Text PODEPSANÉ verze: podpisy ze záznamu se dají na řádky podpisů.
 * `hasSeller` – za prodejce se podepsalo (podpis je v záznamu); jinak řádek zůstane vyplněný tečkami.
 * Vrací i to, které podpisy na řádku skutečně jsou (ostatní se přidají do bloku pod text).
 */
export function prepareSignedText(
  filled: string, templateKey: string | null | undefined, template: string | undefined, hasSeller: boolean,
): { text: string; sellerOnLine: boolean; signerOnLine: boolean } {
  let t = filled;
  if (!hasSeller) {
    t = t.split(SELLER_SIG_TOKEN).join(DOTS);
  } else if (!t.includes(SELLER_SIG_TOKEN) && !(template && template.includes('{{seller_signature}}'))) {
    t = t.replace(SELLER_LINE, (_m, who: string, sep: string) => `${who}${sep}${SELLER_SIG_TOKEN}`);
  }
  t = applySignerSignature(t, templateKey, template);
  return { text: t, sellerOnLine: t.includes(SELLER_SIG_TOKEN), signerOnLine: t.includes(SIGNER_SIG_TOKEN) };
}
